import React, { useState, useEffect } from 'react';
import { IoDocumentTextOutline, IoTimeOutline, IoCheckmarkCircleOutline, IoCloseCircleOutline, IoHourglassOutline, IoChevronBackOutline, IoChevronForwardOutline } from 'react-icons/io5';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

export default function ClosedPositionsContent() {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // API'den veri çekme
    useEffect(() => {
        // İlk yüklemede tüm verileri getir
        fetchClosedPositions();
    }, []);

    const fetchClosedPositions = async (startTime = '', endTime = '') => {
        try {
            setLoading(true);
            setError(null);

            const result = await api.getClosedPositions(startTime, endTime);

            if (result.success) {
                setPositions(result.data || []);
            } else {
                throw new Error(result.message || t('closedPositionsContent.dataFetchError'));
            }
        } catch (err) {
            console.error('API Error:', err);
            setError(err.message || t('closedPositionsContent.errorLoadingData'));
            toast.error(t('closedPositionsContent.errorLoadingData'));
        } finally {
            setLoading(false);
        }
    };

    // Tarih filtreleme fonksiyonu
    const handleDateFilter = async () => {
        if (!startDate || !endDate) {
            toast.error(t('closedPositionsContent.dateFilter.selectDatesError'));
            return;
        }

        if (new Date(startDate) > new Date(endDate)) {
            toast.error(t('closedPositionsContent.dateFilter.invalidDateRangeError'));
            return;
        }

        setIsSearching(true);
        await fetchClosedPositions(startDate, endDate);
        setIsSearching(false);
    };

    // Tarih filtrelerini temizle
    const clearFilters = () => {
        setStartDate('');
        setEndDate('');
        fetchClosedPositions();
    };

    // Tarih formatını düzenleme
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    // Action type'ı metne çevirme
    const getActionTypeText = (actionType) => {
        switch (actionType) {
            case 0:
                return t('closedPositionsContent.actionTypes.buy');
            case 1:
                return t('closedPositionsContent.actionTypes.sell');
            default:
                return t('closedPositionsContent.actionTypes.unknown');
        }
    };

    // Kar/zarar rengini belirleme
    const getProfitColor = (profit) => {
        if (profit > 0) return 'text-success';
        if (profit < 0) return 'text-danger';
        return 'text-muted';
    };

    const totalPages = Math.ceil(positions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = positions.slice(startIndex, endIndex);

    return (
        <div className="crypto-withdrawals-content">
            <div className="change-password-header">
                <h1>{t('closedPositionsContent.title')}</h1>
                <p>{t('closedPositionsContent.subtitle')}</p>
            </div>

            {/* Tarih Filtreleme */}
            <div className="date-filter-section">
                <div className="date-filter-container">
                    <div className="date-input-group">
                        <label htmlFor="startDate" className="date-label">
                            {t('closedPositionsContent.dateFilter.startDateLabel')}
                        </label>
                        <input
                            type="date"
                            id="startDate"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="date-input"
                        />
                    </div>
                    <div className="date-input-group">
                        <label htmlFor="endDate" className="date-label">
                            {t('closedPositionsContent.dateFilter.endDateLabel')}
                        </label>
                        <input
                            type="date"
                            id="endDate"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="date-input"
                        />
                    </div>
                    <div className="date-filter-actions">
                        <button
                            onClick={handleDateFilter}
                            disabled={isSearching || !startDate || !endDate}
                            className="filter-btn"
                        >
                            {isSearching ? (
                                <>
                                    <div className="spinner"></div>
                                    {t('closedPositionsContent.dateFilter.searching')}
                                </>
                            ) : (
                                t('closedPositionsContent.dateFilter.filterButton')
                            )}
                        </button>
                        <button
                            onClick={clearFilters}
                            className="clear-btn"
                        >
                            {t('closedPositionsContent.dateFilter.clearButton')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="content-body">
                {loading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>{t('closedPositionsContent.loading')}</p>
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <p>{t('closedPositionsContent.error')} {error}</p>
                        <button onClick={fetchClosedPositions} className="retry-btn">
                            {t('closedPositionsContent.retryButton')}
                        </button>
                    </div>
                ) : (
                    <div className="closed-positions-table-container">
                        {currentData.length === 0 ? (
                            <div className="no-data">
                                <div className="no-data-content">
                                    <IoDocumentTextOutline size={48} />
                                    <p>{t('closedPositionsContent.noDataMessage')}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="closed-positions-table">
                                {/* Header */}
                                <div className="closed-positions-table-header">
                                    <div className="closed-positions-table-cell">{t('closedPositionsContent.tableHeaders.instrument')}</div>
                                    <div className="closed-positions-table-cell">{t('closedPositionsContent.tableHeaders.actionType')}</div>
                                    <div className="closed-positions-table-cell">{t('closedPositionsContent.tableHeaders.amount')}</div>
                                    <div className="closed-positions-table-cell">{t('closedPositionsContent.tableHeaders.openPrice')}</div>
                                    <div className="closed-positions-table-cell">{t('closedPositionsContent.tableHeaders.closePrice')}</div>
                                    <div className="closed-positions-table-cell">{t('closedPositionsContent.tableHeaders.profitLoss')}</div>
                                    <div className="closed-positions-table-cell">{t('closedPositionsContent.tableHeaders.openDate')}</div>
                                    <div className="closed-positions-table-cell">{t('closedPositionsContent.tableHeaders.closeDate')}</div>
                                    {/* <div className="closed-positions-table-cell">{t('closedPositionsContent.tableHeaders.closeReason')}</div> */}
                                </div>

                                {/* Body */}
                                <div className="closed-positions-table-body">
                                    {currentData.map((item) => (
                                        <div key={item.orderID} className="closed-positions-table-row">
                                            <div className="closed-positions-table-cell" data-label={t('closedPositionsContent.tableHeaders.instrument')}>
                                                <div className="coin-info">
                                                    <span className="coin-symbol">{item.instrumentName}</span>
                                                </div>
                                            </div>
                                            <div className="closed-positions-table-cell" data-label={t('closedPositionsContent.tableHeaders.actionType')}>
                                                <div className="amount-info">
                                                    <span className="amount">{getActionTypeText(item.actionType)}</span>
                                                </div>
                                            </div>
                                            <div className="closed-positions-table-cell" data-label={t('closedPositionsContent.tableHeaders.amount')}>
                                                <div className="amount-info">
                                                    <span className="amount">{item.amount}</span>
                                                </div>
                                            </div>
                                            <div className="closed-positions-table-cell" data-label={t('closedPositionsContent.tableHeaders.openPrice')}>
                                                <div className="amount-info">
                                                    <span className="amount">{item.openRate}</span>
                                                </div>
                                            </div>
                                            <div className="closed-positions-table-cell" data-label={t('closedPositionsContent.tableHeaders.closePrice')}>
                                                <div className="amount-info">
                                                    <span className="amount">{item.closeRate}</span>
                                                </div>
                                            </div>
                                            <div className="closed-positions-table-cell" data-label={t('closedPositionsContent.tableHeaders.profitLoss')}>
                                                <div className="amount-info">
                                                    <span className={`amount ${getProfitColor(item.profitInAccountCurrency)}`}>
                                                        {item.profitInAccountCurrency}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="closed-positions-table-cell" data-label={t('closedPositionsContent.tableHeaders.openDate')}>
                                                <div className="date-info">
                                                    <span className="date">{formatDate(item.openTime)}</span>
                                                </div>
                                            </div>
                                            <div className="closed-positions-table-cell" data-label={t('closedPositionsContent.tableHeaders.closeDate')}>
                                                <div className="date-info">
                                                    <span className="date">{formatDate(item.closeTime)}</span>
                                                </div>
                                            </div>
                                            {/* <div className="closed-positions-table-cell" data-label={t('closedPositionsContent.tableHeaders.closeReason')}>
                                                <div className="amount-info">
                                                    <span className="amount">{item.closingReason}</span>
                                                </div>
                                            </div> */}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="pagination-container">
                                <div className="pagination">
                                    <button
                                        className="pagination-btn prev-btn"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <IoChevronBackOutline size={16} />
                                    </button>

                                    {/* Show current page and next 2 pages */}
                                    {Array.from({ length: Math.min(3, totalPages - currentPage + 1) }, (_, i) => {
                                        const page = currentPage + i;
                                        if (page > totalPages) return null;
                                        return (
                                            <button
                                                key={page}
                                                className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                                                onClick={() => setCurrentPage(page)}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}

                                    <button
                                        className="pagination-btn next-btn"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <IoChevronForwardOutline size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
